/*
 * ============================================================
 * argile-slider.js — slider horizontal (scroll-snap) réutilisable
 * ============================================================
 * Chargé PAR les sections qui l'utilisent (cf. sections/argile-trends.liquid).
 *
 * Cible `[data-argile-slider]` ; défilement PAR PAGE (autant de cartes que le
 * viewport en montre entièrement), un dot par page, flèches prev/next.
 * Structure attendue :
 *   [data-argile-slider]           ← `data-slider-loop` pour navigation cyclique
 *     [data-slider-track] > [data-slider-slide]*
 *     [data-slider-dots] (rempli par le JS)
 *     [data-slider-prev] [data-slider-next]
 *
 * Défilement par page :
 *   - « cartes par page » = nb de cartes qui tiennent ENTIÈREMENT dans le track
 *     (recalculé au resize : 2 sur mobile, 4 sur desktop, etc.).
 *   - flèche = saut d'une page (la 2ᵉ page démarre sur la carte nᵒ perPage+1).
 *   - dots = ceil(nb cartes / cartes par page) → un dot par page.
 *   - le scroll se BLOQUE au bord droit des cartes (dernière page flush à droite,
 *     pas de spacer qui isolerait la dernière carte au bord gauche).
 */
(function () {
  'use strict';

  function initSliders(root) {
    var scope = root || document;
    var sliders = scope.querySelectorAll('[data-argile-slider]');
    if (!sliders.length) return;

    sliders.forEach(function (slider) {
      if (slider.dataset.argileWired) return;
      slider.dataset.argileWired = '1';

      var track = slider.querySelector('[data-slider-track]');
      if (!track) return;
      var slides = [].slice.call(track.querySelectorAll('[data-slider-slide]'));
      if (!slides.length) return;

      var loop = slider.hasAttribute('data-slider-loop');
      var prev = slider.querySelector('[data-slider-prev]');
      var next = slider.querySelector('[data-slider-next]');
      var dotsWrap = slider.querySelector('[data-slider-dots]');
      // Nav (dots + flèches) = parent du conteneur de dots.
      var nav = dotsWrap ? dotsWrap.parentElement : null;

      // Position de scroll d'une carte (basée sur offsetLeft, fiable — pas
      // getBoundingClientRect qui est flou pendant une animation).
      function slidePos(i) {
        return slides[i].offsetLeft - slides[0].offsetLeft;
      }

      // Pas entre deux cartes (largeur carte + gap). Fallback = largeur carte.
      function slideStep() {
        if (slides.length > 1) return slidePos(1) - slidePos(0);
        return slides[0].getBoundingClientRect().width || 1;
      }

      // Scroll max atteignable (bord droit des cartes flush au bord du track).
      function maxScroll() {
        return Math.max(0, track.scrollWidth - track.clientWidth);
      }

      // Cartes visibles ENTIÈREMENT dans le viewport. (clientWidth + gap) / step
      // donne pile N quand N cartes + (N−1) gaps remplissent le track.
      function perPage() {
        var step = slideStep();
        if (step <= 0) return 1;
        var gap = step - slides[0].getBoundingClientRect().width;
        return Math.max(1, Math.floor((track.clientWidth + gap) / step));
      }

      function pageCount() {
        return Math.max(1, Math.ceil(slides.length / perPage()));
      }

      // Page courante déduite du scroll (dernière page si on touche le bord droit).
      function currentPage() {
        var pages = pageCount();
        if (track.scrollLeft >= maxScroll() - 1) return pages - 1;
        var pageWidth = perPage() * slideStep();
        return Math.max(0, Math.min(pages - 1, Math.round(track.scrollLeft / pageWidth)));
      }

      function goToPage(p) {
        var pages = pageCount();
        // Navigation cyclique si `data-slider-loop`, sinon bornée.
        if (loop) {
          p = ((p % pages) + pages) % pages;
        } else {
          p = Math.max(0, Math.min(pages - 1, p));
        }
        var target = Math.min(slidePos(p * perPage()), maxScroll());
        track.scrollTo({ left: target, behavior: 'smooth' });
      }

      function buildDots() {
        if (!dotsWrap) return;
        var pages = pageCount();
        // Ne reconstruire que si le nombre de pages a changé (resize).
        if (dotsWrap.children.length === pages) return;
        dotsWrap.innerHTML = '';
        for (var i = 0; i < pages; i++) {
          (function (page) {
            var dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'argile-slider-dot';
            dot.setAttribute('aria-label', 'Aller à la page ' + (page + 1));
            dot.addEventListener('click', function () { goToPage(page); });
            dotsWrap.appendChild(dot);
          })(i);
        }
      }

      function update() {
        var c = currentPage();
        var pages = pageCount();
        if (dotsWrap) {
          [].forEach.call(dotsWrap.children, function (dot, i) {
            dot.classList.toggle('is-active', i === c);
            dot.setAttribute('aria-current', i === c ? 'true' : 'false');
          });
        }
        // En boucle, les flèches ne sont jamais désactivées (cycliques).
        if (prev) prev.disabled = !loop && c <= 0;
        if (next) next.disabled = !loop && c >= pages - 1;
      }

      // Masque la nav quand rien ne déborde ; (re)construit les dots.
      function sync() {
        var overflowing = maxScroll() > 1;
        if (nav) nav.style.display = overflowing ? '' : 'none';
        buildDots();
      }

      if (prev) prev.addEventListener('click', function () { goToPage(currentPage() - 1); });
      if (next) next.addEventListener('click', function () { goToPage(currentPage() + 1); });

      var ticking = false;
      track.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () { update(); ticking = false; });
      }, { passive: true });

      window.addEventListener('resize', function () { sync(); update(); });
      sync();
      update();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initSliders(document); });
  } else {
    initSliders(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initSliders(event.target);
  });
})();
